import { Signal } from "../core/Signal";

export interface SwipeEvent {
  dx: number;
  dy: number;
  distance: number;
  angle: number;
  durationMs: number;
}

export interface PointerEvt {
  x: number;
  y: number;
  pointerType: string;
}

const SWIPE_THRESHOLD_PX = 30;
const SWIPE_MAX_MS = 500;

export class InputService {
  readonly keyDown = new Signal<KeyboardEvent>();
  readonly keyUp = new Signal<KeyboardEvent>();
  readonly anyInput = new Signal<void>();
  readonly tap = new Signal<PointerEvt>();
  readonly swipe = new Signal<SwipeEvent>();

  private target: HTMLElement;
  private pointerStart: { x: number; y: number; t: number; id: number } | null = null;

  constructor(target: HTMLElement | Window = window) {
    this.target = (target as HTMLElement) ?? document.body;
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);

    const t = this.target;
    t.addEventListener("pointerdown", this.onPointerDown as EventListener, { passive: false });
    t.addEventListener("pointerup", this.onPointerUp as EventListener, { passive: false });
    t.addEventListener("pointercancel", this.onPointerCancel as EventListener);

    t.addEventListener("touchstart", this.preventDefault, { passive: false });
    t.addEventListener("touchmove", this.preventDefault, { passive: false });
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    this.keyDown.emit(e);
    this.anyInput.emit();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keyUp.emit(e);
  };

  private onPointerDown = (e: PointerEvent): void => {
    this.pointerStart = { x: e.clientX, y: e.clientY, t: performance.now(), id: e.pointerId };
  };

  private onPointerUp = (e: PointerEvent): void => {
    const start = this.pointerStart;
    this.pointerStart = null;
    if (!start || start.id !== e.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const distance = Math.hypot(dx, dy);
    const durationMs = performance.now() - start.t;
    if (distance >= SWIPE_THRESHOLD_PX && durationMs <= SWIPE_MAX_MS) {
      this.swipe.emit({
        dx,
        dy,
        distance,
        angle: Math.atan2(dy, dx),
        durationMs,
      });
    } else {
      this.tap.emit({ x: e.clientX, y: e.clientY, pointerType: e.pointerType });
    }
    this.anyInput.emit();
  };

  private onPointerCancel = (): void => {
    this.pointerStart = null;
  };

  private preventDefault = (e: Event): void => {
    e.preventDefault();
  };

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    const t = this.target;
    t.removeEventListener("pointerdown", this.onPointerDown as EventListener);
    t.removeEventListener("pointerup", this.onPointerUp as EventListener);
    t.removeEventListener("pointercancel", this.onPointerCancel as EventListener);
    t.removeEventListener("touchstart", this.preventDefault);
    t.removeEventListener("touchmove", this.preventDefault);
    this.keyDown.clear();
    this.keyUp.clear();
    this.anyInput.clear();
    this.tap.clear();
    this.swipe.clear();
  }
}
